import { ArrayUtils, engineComponent, NumberUtils } from "neo-common";
import { Communication } from "neo-games";
import { ComponentTypes } from "../Components/ComponentTypes";
import { Configuration, GeneralConfigurationAttributes } from "../Components/Configuration";
import { ContainerElement } from "../Components/Elements/Graphics/ContainerElement";
import { TimelineElement } from "../Components/Elements/Graphics/TimelineElement";
import { CreateFrameTimer, FrameTimerEventEmitter, FrameTimerEvents } from "../Utils/FrameTimer";
import { Languages } from "../Utils/Languages";
import { BetManager } from "../Managers/BetManager";
import { ImageTextCounterElement } from "../Components/Elements/Texts/ImageTextCounterElement";
import { AppContext } from "../../Common/AppContext";
import { IPickRoundSpot } from "neo-games/Engine/Communication/Game/Types/IPickRoundSpot";
import { GameManager } from "../Managers/GameManager";
import { Spot } from "../GameComponents/Pick/Spot";
import { PickView } from "../GameComponents/Pick/PickView";
import { Symbol } from "../GameComponents/Symbol";
import { WinAnimator } from "../GameComponents/WinAnimator";
import { Symbols } from "../GameComponents/Symbols";
import { IPickRound } from "neo-games/Engine/Communication/Game/Types/IPickRound";
import { Payline } from "../GameComponents/Payline";
import { PaylinesDefinitions } from "../GameComponents/PaylinesDefinitions";
import { Tweens } from "../Components/Effects/Tweens";
import { Settings } from "./Settings";
import { BaseView } from "../GameComponents/Base/BaseView";
import { SwitchElement } from "../Components/Elements/Graphics/SwitchElement";
import { ImageElement } from "../Components/Elements/Graphics/ImageElement";
import { PlayList } from "../Components/Sounds/PlayList";

var PickRoundSpotStatus = Communication.Game.PickRoundSpotStatus;
var PickRoundSpotValueType = Communication.Game.PickRoundSpotValueType;

/**
 * WinAnimatorPick uses paylines and symbols to create presentation of wins to user.
 * Also handle high wins animation for presentation special screens to user when win is bigger than some defined values.
 * @class WinAnimatorPick
 * @extends ContainerElement
 */
@engineComponent(ComponentTypes.DisplayObject)
export class WinAnimatorPick extends WinAnimator {
    //******************************************************************************************************************
    //  EVENTS
    //******************************************************************************************************************

    //******************************************************************************************************************
    //  TIMER STATUSES
    //******************************************************************************************************************

    protected static readonly Start: string = "start";
    protected static readonly SelectUpdate: string = "selectUpdate";
    protected static readonly Select: string = "select";
    protected static readonly UpdateCounters: string = "updateCounters";
    protected static readonly ShowWin: string = "showWin";

    protected static readonly Idle: string = "idle";

    protected static readonly CycleWins: string = "cycleWins";

    //******************************************************************************************************************
    //  VARIABLES
    //******************************************************************************************************************

    protected elementInstance: Array<Object> = [];

    protected winStepDuration: number = 10;

    protected spotsCount: number = 1; // spots per reel
    protected spotsReelsPosition: Array<number> = [];

    //private _spots: Array<Spot> = [];
    private _spots: Symbols;
    private _pickSelected: number = 0;
    private _actualReel: number = -1; // reel ID where spot is placed
    protected currentSpots: IPickRoundSpot[];
    protected currentRound: IPickRound;

    protected multipliersExtended: Array<string>;
    protected winsSymbolsCount: Array<string>;

    //******************************************************************************************************************
    //  CREATE, INITIALIZE
    //******************************************************************************************************************

    public createByNode(node: Element): void {
        super.createByNode(node);
    }

    protected configure(node: Element): void {
        super.configure(node);

        if (node.hasAttribute("WinsMultipliersExtended")) {
            this.multipliersExtended = ArrayUtils.stringArray(node.getAttribute("WinsMultipliersExtended"), "|");
        }

        if (node.hasAttribute(GeneralConfigurationAttributes.WINS_ANIMATIONS)) {
            this.animationNames = ArrayUtils.stringArray(
                node.getAttribute(GeneralConfigurationAttributes.WINS_ANIMATIONS),
            );
        }

        if (node.hasAttribute("WinsSymbolsCount")) {
            this.winsSymbolsCount = ArrayUtils.stringArray(node.getAttribute("WinsSymbolsCount"));
        }
    }

    public initialize(spots: Symbols): void {
        //this._spots = spots;

        this.createTimer();
        this.createSpots(spots);
        this.createAnimations();
    }

    /**
     *
     * @param {Object} element
     */
    public addElementInstance(element: Object): void {
        this.elementInstance[Object.keys(element)[0]] = element[Object.keys(element)[0]];
    }

    /**
     *
     * @param name
     */
    public getElementInstance(name: string): Object {
        return this.elementInstance[name];
    }

    /**
     * Shows only bonus symbols with position tween into middle row. Other symbols hidden by animation.
     */
    public showOnlyBonusSymbols(): void {
        if (
            GameManager.baseResponse.screen != null &&
            GameManager.baseResponse.screen.reels != null &&
            GameManager.baseResponse.screen.reels.length !== 0
        ) {
            let baseReelsData = GameManager.baseResponse.screen.reels;
            for (let i = 0; i <= 4; i++) {
                for (let j = 0; j < this.currentSymbols[i].length; j++) {
                    if (this.currentSymbols[i][j] != null) {
                        for (let reelIndex = 0; reelIndex < baseReelsData.length; reelIndex++) {
                            for (
                                let symbolIndex = baseReelsData[reelIndex].symbols.length - 1;
                                symbolIndex >= 0;
                                symbolIndex--
                            ) {
                                if (baseReelsData[reelIndex].symbols[symbolIndex] == Settings.SymbolBonus) {
                                    let symbol = this.currentSymbols[reelIndex][0];
                                    symbol.goToCase(Symbol.CaseNormal);

                                    Tweens.removeTweens(symbol, "position");
                                    Tweens.positionTween(symbol, {
                                        tweenX: [symbol.position.x, this.reelX[reelIndex] - symbol.offsetX],
                                        tweenY: [
                                            symbol.position.y,
                                            this.reelY[reelIndex] + this.symbolHeight * symbolIndex - symbol.offsetY,
                                        ],
                                        tweenDuration: 30,
                                    });
                                }
                            }
                        }
                    }
                }
            }
        }
    }

    /**
     *
     * @param spotsData
     */
    public showSpots(spotsData: IPickRoundSpot[]): void {
        this._actualReel = -1;
        this.currentSpots = spotsData;
        let bonusSymbolsCounter: number = 0;

        /*for (let key in spotsData) {
            this.removeCurrentSpot(spotsData[key].id);
        }*/

        this.removeSpots();

        for (let key in spotsData) {
            if (this.currentSpots.length == 3 || this.currentSpots.length == 4) {
                this.spotsReelsPosition[spotsData[key].id] = bonusSymbolsCounter + 1;
            } else {
                this.spotsReelsPosition[spotsData[key].id] = bonusSymbolsCounter;
            }
            bonusSymbolsCounter++;
            this.addCurrentSpot(spotsData[key].id, 0);
        }
    }

    /**
     *
     * @param spotsData
     */
    public setSpotsData(spotsData: IPickRoundSpot[]): void {
        this.currentSpots = spotsData;
    }

    /**
     *
     * @param spotId
     * @param useValue
     */
    protected winSpotsGoToCase(spotId: number, useValue: boolean, init: boolean = false): void {
        let id: number;
        let status: Communication.Game.PickRoundSpotStatus;
        let value: number = null;
        let valueType: Communication.Game.PickRoundSpotValueType;
        let addDelay: boolean = true;

        let spotData: IPickRoundSpot = this.currentSpots.filter((currentSpot) => currentSpot.id == spotId)[0];
        if (spotData.id != null) {
            id = spotData.id;
        }
        if (spotData.status != null) {
            status = spotData.status;
        }
        if (spotData.value != null) {
            value = parseInt(spotData.value + "");
        }
        if (spotData.valueType != null) {
            valueType = spotData.valueType;
        }

        if (spotId != null && status != null) {
            if (this.currentSymbols != null && this.currentSymbols[spotId] != null) {
                switch (status.toString()) {
                    case PickRoundSpotStatus.Enabled.toString():
                        if (this.noWinSymbolsContainer != null) {
                            this.noWinSymbolsContainer.addChild(this.currentSymbols[id][0]);
                        }
                        this.currentSymbols[spotId][0].goToCase("BONUS_ENABLED");
                        break;
                    case PickRoundSpotStatus.Disabled.toString():
                        if (this.noWinSymbolsContainer != null) {
                            this.noWinSymbolsContainer.addChild(this.currentSymbols[id][0]);
                        }
                        this.currentSymbols[spotId][0].goToCase("BONUS_DISABLED");
                        break;
                    case PickRoundSpotStatus.Revealed.toString():
                        this._pickSelected++;
                        this._actualReel = spotId;
                        if (value != null && valueType != null) {
                            if (this.symbolsContainer != null) {
                                this.symbolsContainer.addChild(this.currentSymbols[id][0]);
                            }

                            let currentSpotMultiplier = 0;
                            for (let i = 0; i < this.multipliers.length; i++) {
                                if (value >= this.multipliers[i]) {
                                    currentSpotMultiplier = i;
                                }
                            }

                            let vinyl = <SwitchElement>this.currentSymbols[spotId][0].getChildElementByName("Vinyl");
                            if (vinyl != null) {
                                vinyl.visible = true;
                                vinyl.goToCase(this.animationNames[currentSpotMultiplier].toUpperCase());
                            }

                            //if (valueType === PickRoundSpotValueType.Cash) {
                            let winCounter = <ImageTextCounterElement>(
                                this.currentSymbols[spotId][0].getChildElementByName("WinCounter")
                            );
                            if (AppContext.gameConfiguration.UseCoins && BetManager.CoinSize != null) {
                                ImageTextCounterElement.Round = true;
                                if (winCounter != null) {
                                    winCounter.start(
                                        0,
                                        parseInt(
                                            NumberUtils.devide(BetManager.Bet * value, BetManager.CoinSize).toString(),
                                        ),
                                        0,
                                    );
                                }
                            } else {
                                ImageTextCounterElement.Round = false;
                                if (winCounter != null) {
                                    winCounter.start(
                                        0,
                                        parseFloat(
                                            Languages.formatText(
                                                Languages.FormatMoney,
                                                (BetManager.Bet * value).toString(),
                                            ),
                                        ),
                                        0,
                                    );
                                }
                            }
                            if (init) {
                                this.currentSymbols[spotId][0].goToCase("BONUS_REVEALED");
                            } else {
                                this.currentSymbols[spotId][0].goToCase("BONUS_REVEAL");
                            }
                            //}
                        }
                        break;
                    case PickRoundSpotStatus.Shown.toString():
                        addDelay = false;

                        if (value != null) {
                            //if (useValue
                            let currentSpotMultiplier = 0;
                            for (let i = 0; i < this.multipliers.length; i++) {
                                if (value >= this.multipliers[i]) {
                                    currentSpotMultiplier = i;
                                }
                            }

                            let vinyl = <SwitchElement>this.currentSymbols[spotId][0].getChildElementByName("Vinyl");
                            if (vinyl != null) {
                                vinyl.visible = true;
                                vinyl.goToCase(this.animationNames[currentSpotMultiplier].toUpperCase() + "_NOWIN");
                            }

                            let winCounter = <ImageTextCounterElement>(
                                this.currentSymbols[spotId][0].getChildElementByName("WinCounterDark")
                            );
                            if (AppContext.gameConfiguration.UseCoins && BetManager.CoinSize != null) {
                                ImageTextCounterElement.Round = true;
                                if (winCounter != null) {
                                    winCounter.start(
                                        0,
                                        parseInt(
                                            NumberUtils.devide(BetManager.Bet * value, BetManager.CoinSize).toString(),
                                        ),
                                        0,
                                    );
                                }
                            } else {
                                ImageTextCounterElement.Round = false;
                                if (winCounter != null) {
                                    winCounter.start(
                                        0,
                                        parseFloat(
                                            Languages.formatText(
                                                Languages.FormatMoney,
                                                (BetManager.Bet * value).toString(),
                                            ),
                                        ),
                                        0,
                                    );
                                }
                            }
                            if (init) {
                                this.currentSymbols[spotId][0].goToCase("BONUS_SHOWN");
                            } else {
                                this.currentSymbols[spotId][0].goToCase("BONUS_SHOW");
                            }
                        }

                        if (this.noWinSymbolsContainer != null) {
                            this.noWinSymbolsContainer.addChild(this.currentSymbols[id][0]);
                        }
                        break;
                    default:
                        break;
                }
            } else {
                if (DEBUG) {
                    this.log("ERROR no spots or spot with id");
                }
            }
        } else {
            if (DEBUG) {
                this.log("ERROR no id or status");
            }
        }
    }

    //******************************************************************************************************************
    //  SHOW WINS
    //******************************************************************************************************************

    /**
     *
     * @param round
     */
    public showPickWins(round: IPickRound): boolean {
        this.currentRound = round;
        this.currentWin = round.totalWin;

        if (round.totalWin != null && round.totalWin > 0) {
            this.currentMultiplierIndex = 0;

            let indx = this.winsSymbolsCount.indexOf(round.spots.length.toString());
            this.multipliers = ArrayUtils.numberArray(this.multipliersExtended[indx]);

            for (let i = 0; i < this.multipliers.length; i++) {
                if (this.currentWin / BetManager.Bet >= this.multipliers[i]) {
                    this.currentMultiplierIndex = i;
                }
            }

            this.showFirstWins();

            return this.currentMultiplierIndex >= 3;
        } else {
            //this.allSymbolsGoToCase(Symbol.CaseNormal, false);
            this.startTimer(WinAnimatorPick.Idle, this.getInitialIdleDelay());

            return false;
        }
    }

    public showPickInitWins(round: IPickRound): void {
        this.currentRound = round;
        this.currentWin = round.totalWin;

        if (round.totalWin != null && round.totalWin > 0) {
            this.currentMultiplierIndex = 0;

            let indx = this.winsSymbolsCount.indexOf(round.spots.length.toString());
            this.multipliers = ArrayUtils.numberArray(this.multipliersExtended[indx]);

            for (let i = 0; i < this.multipliers.length; i++) {
                if (this.currentWin / BetManager.Bet >= this.multipliers[i]) {
                    this.currentMultiplierIndex = i;
                }
            }

            this.showFirstInitWins();
        } else {
            this.startTimer(WinAnimatorPick.Idle, this.getInitialIdleDelay());
        }
    }

    protected showFirstInitWins(): void {
        for (let key in this.currentSpots) {
            // Add spot to win case
            this.winSpotsGoToCase(this.currentSpots[key].id, false, true);
        }
    }

    protected showFirstWins(): void {
        for (let key in this.currentSpots) {
            // Add spot to win case
            this.winSpotsGoToCase(this.currentSpots[key].id, false);
        }

        this.startAnimation();
    }

    /**
     * 1) sets dynamic timer to delay win animation start
     * 2) plays animation
     * 3) make position tween to "center" of the screen
     */
    protected startAnimation(): void {
        this.currentAnimationIndex = this.getAnimationIndex();
        this.currentAnimationFinished = false;

        if (this.currentAnimationIndex < 3) {
            PlayList.play("SND_BonusSelect");
        } else {
            PlayList.play("SND_BonusSelectDiamond");
        }

        const animation = this.animations[this.currentAnimationIndex];
        animation.visible = true;
        animation.setDynamicTimeFrames(47).updateDynamicTimeFrames();

        if (AppContext.gameConfiguration.UseCoins && BetManager.CoinSize != null) {
            ImageTextCounterElement.Round = true;
            animation.play(parseInt(NumberUtils.devide(this.currentWin, BetManager.CoinSize).toString()));
        } else {
            ImageTextCounterElement.Round = false;
            animation.play(this.currentWin);
        }

        Tweens.removeTweens(animation);
        Tweens.positionTween(animation, {
            tweenX: [this.currentSymbols[this._actualReel][0].position.x, this.reelX[2] + this.symbolWidth / 2], // from position of the SPOT to position of the middle reel
            tweenY: [2 * this.symbolHeight + (1 / 2) * this.symbolHeight, 2 * this.symbolHeight],
            tweenDuration: 60,
            tweenDelay: 77,
        });
        Tweens.scaleTween(animation, {
            tweenValues: [new PIXI.Point(0.25, 0.25), new PIXI.Point(1, 1)],
            tweenDuration: 60,
            tweenDelay: 77,
        });
    }

    protected onAnimationFinished(): void {
        super.onAnimationFinished();

        if (this.currentMultiplierIndex >= 3) {
            let winAnimation = this.getElementInstance("WinAnimation") as SwitchElement;
            winAnimation.goToCase("PLAY");
        }
    }

    /**
     * 1) return win animation to original position
     * 2) finish win animator
     */
    protected onAnimationComplete(): void {
        if (this.currentAnimationIndex == 3) {
            PlayList.play("SND_BonusFX03diamond");
        } else {
            PlayList.play("SND_BonusFX03");
        }

        const animation = this.animations[this.currentAnimationIndex];
        Tweens.removeTweens(animation);
        Tweens.positionTween(animation, {
            tweenX: [
                animation.position.x,
                this.reelX[this.spotsReelsPosition[this._actualReel]] + this.symbolWidth / 2,
            ], // from position of the middle reel to upper position of the SPOT
            tweenY: [animation.position.y, this.symbolHeight],
            tweenDuration: 5,
        });
        Tweens.scaleTween(animation, {
            tweenValues: [new PIXI.Point(1, 1), new PIXI.Point(0.3, 0.3)],
            tweenDuration: 5,
            callbackScope: this,
            callbackFunction: () => {
                animation.visible = false;
            },
        });

        if (this.currentMultiplierIndex >= 3) {
            let winAnimation = this.getElementInstance("WinAnimation") as SwitchElement;
            winAnimation.goToCase("STOPPING");
        }

        this.currentMultiplierIndex = -1;
        this.currentAnimationIndex = null;

        this.finishFirstWins();
    }

    //******************************************************************************************************************
    //  RESET
    //******************************************************************************************************************

    public reset(showLastSymbols: boolean, spin: boolean = false): boolean {
        // Timer
        this.stopTimer();

        if (showLastSymbols && !spin) {
            this.startTimer(WinAnimatorPick.Idle, this.getInitialIdleDelay());
        }

        //this.value.visible = false;

        //this.removeSpots();

        let lastMultiplierIndex = this.currentMultiplierIndex;
        this.currentMultiplierIndex = -1;

        if (this.currentAnimationIndex != null) {
            if (this.currentAnimationFinished) {
                this.resetAnimations();

                this.currentWins = null;
                this.currentWin = null;

                return false;
            } else {
                this.stopAnimation();

                this.currentWins = null;
                this.currentWin = null;

                return lastMultiplierIndex >= 3;
            }
        } else {
            this.currentWins = null;
            this.currentWin = null;

            return lastMultiplierIndex >= 3;
        }
    }

    //******************************************************************************************************************
    //  TIMER
    //******************************************************************************************************************

    protected onTimerComplete(): void {
        this.timer.stop();

        switch (this.timerStatus) {
            case WinAnimatorPick.Start:
                break;
            case WinAnimatorPick.SelectUpdate:
                break;
            case WinAnimatorPick.Select:
                break;
            case WinAnimatorPick.UpdateCounters:
                break;
            case WinAnimatorPick.ShowWin:
                //this.showWins();
                break;
            case WinAnimatorPick.Idle:
                this.idle();
                break;
        }
    }

    //******************************************************************************************************************
    //  SYMBOLS
    //******************************************************************************************************************

    protected createSpots(symbols: Symbols): void {
        this.symbols = symbols;

        this.noWinSymbolsContainer = new ContainerElement();
        this.noWinSymbolsContainer.name = "NoWinSymbols";
        this.addChild(this.noWinSymbolsContainer);

        this.symbolsContainer = new ContainerElement();
        this.symbolsContainer.name = "Symbols";
        this.addChild(this.symbolsContainer);

        this.currentSymbols = [];

        for (let i = 0; i < this.reelCount; i++) {
            this.currentSymbols.push([]);

            for (let j = 0; j < this.symbolCount; j++) {
                this.currentSymbols[i].push(null);
            }
        }
    }

    /**
     *
     * @param reelIndex
     * @param symbolIndex
     */
    protected addCurrentSpot(reelIndex: number, symbolIndex: number = 0): void {
        if (this.currentSymbols[reelIndex] != null) {
            if (this.currentSymbols[reelIndex][symbolIndex] === null) {
                let spotData: IPickRoundSpot = this.currentSpots.filter(
                    (currentSpot) => currentSpot.id == reelIndex,
                )[0];
                if (spotData != null && spotData.id != null && spotData.status != null) {
                    switch (spotData.status.toString()) {
                        case PickRoundSpotStatus.Enabled.toString():
                            if (this.symbolsContainer != null) {
                                const symbol: Symbol = this.symbols.childs["Spot_" + spotData.id];
                                symbol.position.x = this.reelX[this.spotsReelsPosition[spotData.id]] - symbol.offsetX;
                                symbol.position.y = this.reelY[reelIndex] + this.symbolHeight - symbol.offsetY;
                                symbol.goToCase("BONUS_INTRO");
                                this.symbolsContainer.addChild(symbol);

                                this.currentSymbols[reelIndex][symbolIndex] = symbol;
                            }

                            if (BaseView.ReelLights != null) {
                                if (BaseView.ReelLights[spotData.id] != null) {
                                    BaseView.ReelLights[spotData.id].alpha = 1;
                                    BaseView.ReelLights[spotData.id].goToCase("ENABLED");
                                    BaseView.ReelLights[spotData.id].position.x =
                                        this.reelX[this.spotsReelsPosition[spotData.id]] + this.reelX[0] / 2;
                                }
                            }
                            break;
                        case PickRoundSpotStatus.Revealed.toString():
                            if (this.symbolsContainer != null) {
                                const symbol: Symbol = this.symbols.childs["Spot_" + spotData.id];
                                symbol.position.x = this.reelX[this.spotsReelsPosition[spotData.id]] - symbol.offsetX;
                                symbol.position.y = this.reelY[reelIndex] + this.symbolHeight - symbol.offsetY;
                                symbol.goToCase("BONUS_REVEALED");
                                this.symbolsContainer.addChild(symbol);

                                this.currentSymbols[reelIndex][symbolIndex] = symbol;
                            }
                            break;
                        case PickRoundSpotStatus.Shown.toString():
                            if (this.symbolsContainer != null) {
                                const symbol: Symbol = this.symbols.childs["Spot_" + spotData.id];
                                symbol.position.x = this.reelX[this.spotsReelsPosition[spotData.id]] - symbol.offsetX;
                                symbol.position.y = this.reelY[reelIndex] + this.symbolHeight - symbol.offsetY;
                                symbol.goToCase("BONUS_SHOWN");
                                this.symbolsContainer.addChild(symbol);

                                this.currentSymbols[reelIndex][symbolIndex] = symbol;
                            }
                            break;
                    }
                }
            }
        }
    }

    protected removeCurrentSpot(reelIndex: number, symbolIndex: number = 0): void {
        if (this.currentSymbols[reelIndex][symbolIndex] !== null) {
            this.currentSymbols[reelIndex][symbolIndex] = null;
        }
    }

    protected removeSpots(): void {
        for (let i = 0; i < this.currentSymbols.length; i++) {
            if (this.currentSymbols[i][0] !== null) {
                this.symbols.addChild(this.currentSymbols[i][0]);
                this.currentSymbols[i][0] = null;
            }
        }
    }

    //******************************************************************************************************************
    //  IDLE
    //******************************************************************************************************************

    public idle(): void {
        for (let i = 0; i < this.getIdleActiveElementsCount(); i++) {
            let reelIndex = Math.floor(Math.random() * this.reelCount);
            const symbol = this.currentSymbols[reelIndex][Math.floor(Math.random() * this.spotsCount)];

            if (symbol != null && symbol.getCurrentCase() === "BONUS_ENABLED") {
                if (symbol.hasCase("BONUS_IDLE")) {
                    symbol.goToCase("BONUS_IDLE");
                }
                if (BaseView.ReelLights != null) {
                    for (let j = 0; j < BaseView.ReelLights.length; j++) {
                        if (
                            BaseView.ReelLights[j] != null &&
                            ["IDLE", "IDLE_DELAYED_" + j, "IDLE_" + j, "EMPTY"].indexOf(
                                BaseView.ReelLights[j].getCurrentCase(),
                            ) == -1
                        ) {
                            BaseView.ReelLights[j].setDynamicTime((j + 1) * 5).goToCase("IDLE_DELAYED_" + j);
                        }
                    }
                }
            }
        }
        this.startTimer(WinAnimatorPick.Idle, this.getIdleDelay());
    }

    protected getInitialIdleDelay(): number {
        return 60;
    }

    protected getIdleDelay(): number {
        return 60 * 3;
    }

    protected getIdleActiveElementsCount(): number {
        return 1;
    }
}

export const WinAnimatorPickAttributes = {
    /**
     * Sounds property initialize playlist for winAnimator.
     * @property Sounds
     * @type String
     * @default null
     */
    SOUNDS: "Sounds",
    /**
     * Name of config for win animator.
     * @property Configs
     * @type string
     * @default null
     */
    CONFIGS: "Configs",
};



/** WEBPACK FOOTER **
 ** ./Engine/Game/CustomComponents/WinAnimatorPick.ts
 **/